import { useCallback } from 'react';
import { usePostHog } from '@posthog/react';

export const StoreSource = {
  onboarding: 'onboarding',
  storeList: 'store_list',
  duplicate: 'duplicate',
  autoProvisioned: 'auto_provisioned',
} as const;

export type StoreSourceValue = typeof StoreSource[keyof typeof StoreSource];

interface SignupProperties {
  method: 'email';
  role: string;
  has_next_param: boolean;
  properties_managed: string;
  marketing_consent: boolean;
}

interface OrderProperties {
  store_id: number;
  order_id: number;
  order_total: number;
}

// The tracking plan. Saved insights filter on these exact strings, so a rename breaks an
// insight silently rather than erroring.
export interface AnalyticsEvents {
  signup_completed: SignupProperties;
  user_signed_up: SignupProperties;
  user_logged_in: { role: string };
  social_login_initiated: { provider: 'google' | 'facebook' };

  onboarding_option_clicked: { option: string };
  onboarding_video_played: EmptyProperties;
  onboarding_video_completed: EmptyProperties;

  store_create_submitted: { source: StoreSourceValue };
  store_created: { source: StoreSourceValue; store_id?: number };
  store_create_failed: { source: StoreSourceValue; reason: string };
  store_edited: { store_id: number; fields_changed: string[] };
  stores_imported: { platform: string; store_count: number };

  checkout_started: { store_id: number; order_id: number; item_count: number };
  checkout_submitted: OrderProperties & { is_zero_amount: boolean };
  payment_completed: OrderProperties & { currency: string };
  payment_failed: OrderProperties & { currency: string };

  pricing_interval_changed: { interval: 'month' | 'year' };
  subscription_plan_selected: {
    plan_name: string;
    plan_type: string;
    interval: string;
    price: number;
  };
  subscription_managed: { current_plan: string };
  addon_selected: { addon_name: string; addon_price: number };
}

type EmptyProperties = Record<string, never>;

interface CaptureOptions {
  // PostHog's `$set_once`: person properties written only if not already present.
  setOnce?: Record<string, unknown>;
}

export function useAnalytics() {
  const posthog = usePostHog();

  return useCallback(
    <E extends keyof AnalyticsEvents>(
      event: E,
      properties: AnalyticsEvents[E],
      options?: CaptureOptions,
    ) =>
      posthog?.capture(event, {
        ...properties,
        ...(options?.setOnce ? { $set_once: options.setOnce } : {}),
      }),
    [posthog],
  );
}
